import { useEffect } from 'react';
import {
  CmNetworkPointResponse,
  CapacityManagementRootResponse,
} from 'src/apis/capacity-management/types';
import { CapacityManagementRootRequest } from 'src/apis/capacity-management/use-capacity-management-root';
import { NetworkPoint } from 'src/apis/monolith/types';
import { useAxios } from 'src/hooks/use-axios';
import { NetworkPointRequest } from 'src/pages/transport/network-points/details/use-network-point';
import { Forbidden } from 'src/utils/errors';
import urlTemplate from 'url-template';

type HookConfig = {
  capacityManagementRoot: CapacityManagementRootRequest;
  networkPoint: NetworkPointRequest;
};

type RequestOptions = {
  rootData: CapacityManagementRootResponse;
  networkPointData: NetworkPoint;
};

export function useCmNetworkPoint({
  capacityManagementRoot,
  networkPoint,
}: HookConfig) {
  const cmNetworkPoint = useAxios(
    (axios, signal, { rootData, networkPointData }: RequestOptions) => {
      if (!rootData._links?.networkpoint) throw new Forbidden();

      const url = urlTemplate
        .parse(rootData._links.networkpoint.href)
        .expand({ nwpId: networkPointData.networkPointUuid });

      return axios.request<CmNetworkPointResponse>({
        signal,
        url,
      });
    },
    { neededOnPageLoad: true }
  );

  useEffect(
    () => {
      if (!networkPoint.response) return;
      if (!capacityManagementRoot.response) return;

      cmNetworkPoint.execute({
        rootData: capacityManagementRoot.response.data,
        networkPointData: networkPoint.response.data,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [capacityManagementRoot.response, networkPoint.response]
  );

  return cmNetworkPoint;
}

export type CmNetworkPointRequest = ReturnType<typeof useCmNetworkPoint>;
